import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Phone, Clock, Menu, X } from 'lucide-react';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/regas', label: 'Regas' },
    { path: '/diagnostics', label: 'Diagnostics' },
    { path: '/problems', label: 'Common Problems' },
    { path: '/pricing', label: 'Pricing' },
    { path: '/why-us', label: 'Why Choose Us' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      {/* Top Bar */}
      <div className="bg-slate-800 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap justify-between items-center text-sm">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-slate-400" />
            <span>Unit 2, Hawthorn Road, Eastbourne BN23 6QA</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-400" />
            <span>Mon-Fri 8am-5pm</span>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-12 h-12 bg-slate-800 rounded-lg flex items-center justify-center text-white font-bold text-xl">
              EV
            </div>
            <div className="hidden sm:block">
              <div className="font-semibold text-slate-800 text-lg leading-tight">Eastbourne Vehicle Air</div>
              <div className="font-semibold text-slate-800 text-lg leading-tight">Conditioning</div>
              <div className="text-xs text-slate-500">Service & Repair Specialists</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
                className={`text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-teal-600'
                    : 'text-slate-600 hover:text-teal-600'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Phone & Book Now */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="tel:01323643333"
              className="flex items-center gap-2 text-slate-700 hover:text-teal-600 transition-colors"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" />
              <span className="font-semibold">01323 643333</span>
            </a>
            <Link
              to="/contact"
              className="bg-slate-800 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-slate-700 transition-colors"
              data-testid="book-now-btn"
            >
              Book Now
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t">
            <div className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2 rounded-lg ${
                    isActive(link.path)
                      ? 'bg-teal-50 text-teal-600'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 px-4 border-t">
                <a
                  href="tel:01323643333"
                  className="flex items-center gap-2 text-slate-700 mb-3"
                >
                  <Phone className="w-4 h-4" />
                  <span className="font-semibold">01323 643333</span>
                </a>
                <Link
                  to="/contact"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full bg-slate-800 text-white px-5 py-2.5 rounded-lg font-medium text-center"
                >
                  Book Now
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
